import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import { InfoItem } from '@frk/eds-components';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { UsTaxDistribution } from '@products/models/us-tax';
import { TaxState } from '@products/types/tax.type';
import { TranslateService } from '@shared/translate/translate.service';
import { FundId, ProductDetailConfiguration, ShareClassCode } from '@types';
import { Logger } from '@utils/logger';
import { generateUUID } from '@utils/text/string-utils';
import { Subscription } from 'rxjs';
import { UsTaxService } from '../services/us-tax.service';
import { TaxDisplayComponent } from '../tax-component.interface';
import { TaxConfig } from '../tax.config';
import { SiteConfigService } from '@services';

const logger = Logger.getLogger('USTaxComponent');

interface YearOption {
  label: string;
  value: number;
}

type ColumnDefs = {
  s: string;
  m: string;
  l: string;
};
@Component({
  selector: 'ft-us-tax',
  templateUrl: './us-tax.component.html',
  styleUrls: ['./us-tax.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsTaxComponent
  extends BaseProductDetailComponent
  implements TaxDisplayComponent, OnInit, OnDestroy {
  componentConfiguration: TaxConfig;
  dataSubscription: Subscription;

  distributions: UsTaxDistribution[];
  additional: InfoItem[];
  states: InfoItem[];
  yearOptions: YearOption[] = [];
  yearOptionSelected: number;
  hasDataForCurrentYear = true;
  noDataMessage: string;
  tooltips: { [name: string]: string } = {};
  stateCols = '1';
  federalTaxIdTitle: string;
  pdf: string;
  lmPdfMessage: string;
  statesColumns: ColumnDefs;
  isSiteIntl: boolean;
  private dropdownId = `tax-year-select${generateUUID()}`;

  public get getDropdownId(): string {
    return this.dropdownId;
  }

  constructor(
    private taxService: UsTaxService,
    private translateService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private siteConfigService: SiteConfigService
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.isSiteIntl = this.siteConfigService.isSiteInternational();
    this.tooltips[
      'products.us-tax-additional-section-title'
    ] = this.translateService.tooltip(
      'products.us-tax-additional-section-title'
    );
    this.tooltips[
      'products.us-tax-states-section-title'
    ] = this.translateService.tooltip('products.us-tax-states-section-title');
  }

  populate(
    componentConfig: TaxConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    this.changeDetector.detectChanges();
    if (this.brConfig.fundId) {
      this.dataSubscription = this.taxService.taxState$.subscribe(
        (newState: TaxState) => this.mapState(newState)
      );
      this.taxService.populate(
        {
          fundId: brConfig.fundId,
          shareClassCode: brConfig.shareClassCode,
          year: this.taxService.getLastYear(),
        },
        true
      );
    }
  }

  /**
   * Called when state changes.
   * @param state the new state returned by tax service
   */
  mapState(state: TaxState): void {
    this.federalTaxIdTitle = `${this.translateService.instant(
      'products.us-tax-federal-id-number'
    )} ${state.federalTaxId}`;
    this.additional = state.taxData?.additionalTaxInfo?.items;
    this.states = state.taxData?.states;
    this.yearOptions = state.years.map((year) => ({
      label: String(year),
      value: year,
    }));
    this.yearOptionSelected = state.currentYear;
    this.stateCols = String(state.stateCols);
    this.statesColumns = this.createStatesColumns(state.stateCols);
    this.pdf = state.pdf;

    if (!state.noDataMessage) {
      this.distributions = state.taxData.distributions;
      this.hasDataForCurrentYear = true;
    } else {
      this.hasDataForCurrentYear = false;
      this.noDataMessage = state.noDataMessage;
    }
    this.lmPdfMessage = this.translateService
      .instant('products.us-tax-lm-pdf-download-message')
      .replace('{{year}}', String(state.currentYear));
    this.changeDetector.detectChanges();
  }

  /**
   * Return the title text for the option selected.
   * @param yearOptionSelected year option selected, coming from state
   */
  getTitleText(yearOptionSelected: number): string {
    return this.yearOptions.find(
      (yearOption) => yearOption.value === yearOptionSelected
    )?.label;
  }

  onTaxYearChanged($event) {
    this.yearOptionSelected = $event.value;
    this.taxService.populate(
      {
        fundId: this.brConfig.fundId,
        shareClassCode: this.brConfig.shareClassCode,
        year: $event.value,
      },
      false
    );
  }

  createStatesColumns(cols: number): ColumnDefs {
    switch (cols) {
      case 1:
        return { s: '4', m: '6', l: '4' };
      case 2:
        return { s: '4', m: '8', l: '8' };
      default:
        return { s: '4', m: '8', l: '12' };
    }
  }

  /**
   * Calls a function in service to download data
   */
  public downloadTaxInfoExcel(): void {
    this.taxService.downloadTaxInfoExcel();
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.unsubscribe();
  }
}
